import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useVisibleItemCount } from '@/hooks/use-visible-item-count';
import { cn } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { Manager } from '@/modules/projects/types/project';

interface ManagersOverflowPopoverProps {
  managers: Manager[];
  onOpenChange?: (open: boolean) => void;
}

function ManagersOverflowPopover({ managers, onOpenChange }: ManagersOverflowPopoverProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    onOpenChange?.(value);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        className="outline-none"
        onMouseEnter={() => handleOpenChange(true)}
        onMouseLeave={() => handleOpenChange(false)}
      >
        <Badge variant="secondary" className="cursor-default">{`+${managers.length}`}</Badge>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-40 p-2"
        align="start"
        onMouseEnter={() => handleOpenChange(true)}
        onMouseLeave={() => handleOpenChange(false)}
      >
        <div className="flex flex-col gap-1">
          {managers.map(manager => (
            <div key={manager.id} className="flex items-center gap-2 px-1 py-0.5">
              <EntityAvatar
                image={manager.profileImageUrl}
                name={manager.name}
                size="xs"
                shouldDownscale={false}
              />
              <span className="text-sm">{manager.name}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ManagersCellProps {
  managers: Manager[];
  isArchived: boolean;
}

export function ManagersCell({ managers, isArchived }: ManagersCellProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { containerRef, shadowRef, visibleCount } = useVisibleItemCount(managers.length);
  const hiddenCount = managers.length - visibleCount;

  return (
    <div ref={containerRef} className="relative min-w-0">
      {/* Shadow clone for measuring item widths — invisible and out of flow */}
      <div
        ref={shadowRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute flex gap-2"
      >
        {managers.map(manager => (
          <Badge key={manager.id} variant="secondary" className="gap-1.5 whitespace-nowrap">
            <EntityAvatar
              image={manager.profileImageUrl}
              name={manager.name}
              size="xs"
              shouldDownscale={false}
            />
            {manager.name}
          </Badge>
        ))}
      </div>

      {/*
       * When overflow: items wrapper shrinks (default flex-shrink: 1) and clips content,
       * badge stays adjacent with shrink-0. Scroll disabled when badge is showing
       * because the animation would move the badge out of view.
       */}
      <ScrollableText
        className={cn(isArchived && 'opacity-50')}
        paused={popoverOpen}
        disabled={hiddenCount > 0}
      >
        <div className="flex items-center gap-2">
          <div className={cn('flex gap-2', hiddenCount > 0 && 'min-w-0 overflow-hidden')}>
            {managers.slice(0, visibleCount).map(manager => (
              <Badge key={manager.id} variant="secondary" className="gap-1.5 whitespace-nowrap">
                <EntityAvatar
                  image={manager.profileImageUrl}
                  name={manager.name}
                  size="xs"
                  shouldDownscale={false}
                />
                {manager.name}
              </Badge>
            ))}
          </div>
          {hiddenCount > 0 && (
            <div className="shrink-0">
              <ManagersOverflowPopover
                managers={managers.slice(visibleCount)}
                onOpenChange={setPopoverOpen}
              />
            </div>
          )}
        </div>
      </ScrollableText>
    </div>
  );
}
